import React, { useState, useEffect, useRef } from 'react';
import logo from "../icon/logo.png";

/* ─────────────────────────────────────────────
   DESIGN TOKENS — "Digitalbrutalnft // THE RESERVE"
   Matches login/signup/about/landing.
───────────────────────────────────────────── */
const C = {
  bg:          "#000000",
  surface:     "#0A0A0A",
  surfaceAlt:  "#121212",
  border:      "#1F1F1F",
  borderHover: "#333333",
  accent:      "#C9A15C",
  accentDim:   "rgba(201,161,92,0.10)",
  accentGlow:  "rgba(201,161,92,0.30)",
  text:        "#EDEDEF",
  textMid:     "#9A9AA0",
  textDim:     "#5C5C62",
  red:         "#C9695F",
  redDim:      "rgba(201,105,95,0.12)",
  amber:       "#C9884B",
  amberDim:    "rgba(201,136,75,0.12)",
  blue:        "#8C99A8",
  blueDim:     "rgba(140,153,168,0.12)",
  purple:      "#9C87B8",
  green:       "#6FA383",
};

/* ─────────────────────────────────────────────
   DISCLOSURE CONTENT
───────────────────────────────────────────── */
const DISCLOSURES = [
  {
    id: "risk-warning",
    tag: "REQUIRED",
    tagColor: C.red,
    icon: "⚠️",
    title: "Risk Warning",
    severity: "high",
    summary: "Trading financial instruments carries substantial risk. You may lose all of your invested capital.",
    content: [
      "Trading and investing in financial instruments, including contracts for difference (CFDs), forex, commodities, and equities, involves a high level of risk and may not be suitable for all investors.",
      "The value of your investments can fall as well as rise. You may lose some or, in certain circumstances, all of the capital you invest. Past performance of any instrument, strategy, or signal provider is not a reliable indicator of future results.",
      "Leveraged products can magnify both gains and losses. Before trading on margin, ensure you fully understand the risks and have adequate financial resources to sustain potential losses beyond your initial deposit.",
      "You should not trade with money you cannot afford to lose. If you are in any doubt about the suitability of these products for your personal circumstances, you should seek independent financial, legal, and tax advice.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "regulatory-status",
    tag: "REGULATORY",
    tagColor: C.blue,
    icon: "🏛️",
    title: "Regulatory Status",
    severity: "info",
    summary: "Digitalbrutalnft Technologies Ltd is authorised and regulated by the Financial Conduct Authority.",
    content: [
      "Digitalbrutalnft Technologies Ltd (\"Digitalbrutalnft\") is a company registered in England and Wales (Company No. 12345678), with its registered office at 1 Canada Square, London E14 5AB.",
      "Digitalbrutalnft is authorised and regulated by the Financial Conduct Authority (FCA) of the United Kingdom under Firm Reference Number (FRN) 987654. You can verify our regulatory status at any time by visiting the FCA Register at register.fca.org.uk.",
      "Our FCA authorisation permits us to arrange and communicate financial promotions in relation to investment activities, and to operate the Digitalbrutalnft copy-trading and portfolio management platform.",
      "Digitalbrutalnft is not authorised to provide personal financial advice. Nothing on the Digitalbrutalnft platform constitutes a personal recommendation. All investment decisions are yours alone.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "copy-trading",
    tag: "SERVICE",
    tagColor: C.purple,
    icon: "📋",
    title: "Copy Trading Disclosure",
    severity: "medium",
    summary: "Copying the trades of signal providers does not guarantee you will replicate their results.",
    content: [
      "When you copy a signal provider on Digitalbrutalnft, your account automatically opens and closes positions that mirror those of the signal provider you have selected. However, your actual results may differ materially from those of the signal provider due to factors including, but not limited to: differences in account size and leverage settings, slippage and execution timing, market conditions at the time your copy orders are executed, and the impact of fees and spreads.",
      "Signal providers on Digitalbrutalnft are not employed by or affiliated with Digitalbrutalnft. They are independent traders who have agreed to make their trading activity visible to subscribers. Digitalbrutalnft does not verify the accuracy of any performance statistics displayed on signal provider profiles beyond the automated data our system records.",
      "A signal provider's past performance — including win rate, Sharpe ratio, drawdown figures, and profit history — is historical data only. It does not indicate, predict, or guarantee future performance. Signal providers may change their strategy, take on significantly more risk, or stop trading at any time.",
      "You remain solely responsible for your decision to copy any signal provider and for the trading activity that results from that decision. Digitalbrutalnft is not liable for losses incurred through copy trading.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "hired-trader",
    tag: "SERVICE",
    tagColor: C.purple,
    icon: "👥",
    title: "Hired Trader Disclosure",
    severity: "medium",
    summary: "Delegating your portfolio to a hired trader transfers execution authority but not your financial responsibility.",
    content: [
      "The Hire a Trader feature allows you to delegate trading decisions for your portfolio to a vetted professional trader. When you hire a trader, you grant them discretionary authority to open and close positions in your account within the risk parameters you set at the time of engagement.",
      "Hired traders are independent contractors, not employees or agents of Digitalbrutalnft. Digitalbrutalnft conducts due diligence on traders before listing them on the platform, including identity verification, track record review, and compliance screening, but we cannot guarantee their ongoing performance or conduct.",
      "You retain full ownership of your account and its assets at all times. A hired trader cannot withdraw funds from your account. You may terminate a hired trader mandate at any time from your account settings, subject to any notice period agreed in your mandate terms.",
      "Performance fees payable to hired traders are governed by the mandate agreement between you and the trader. Digitalbrutalnft deducts a 5% intermediary fee from any performance fee charged. All fees are disclosed before you confirm a mandate.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "conflicts",
    tag: "CONDUCT",
    tagColor: C.amber,
    icon: "⚖️",
    title: "Conflicts of Interest",
    severity: "medium",
    summary: "Digitalbrutalnft maintains a Conflicts of Interest Policy and takes steps to manage conflicts fairly.",
    content: [
      "As a regulated firm, Digitalbrutalnft is required to identify, manage, and disclose conflicts of interest that may arise in the course of providing our services. We maintain a formal Conflicts of Interest Policy, reviewed annually by our compliance team.",
      "Potential conflicts on the Digitalbrutalnft platform include: signal providers who may promote their own strategies; Digitalbrutalnft earning subscription and performance-fee revenue that could create an incentive to favour high-fee signal providers; and Digitalbrutalnft staff or directors holding personal trading accounts.",
      "We manage these conflicts through: algorithmic, objective ranking of signal providers based solely on verified performance metrics; a staff trading policy requiring disclosure and pre-approval of personal trades; and separation of our commercial and compliance functions.",
      "If you believe a conflict of interest has disadvantaged you, please contact compliance@Digitalbrutalnft.io. A copy of our full Conflicts of Interest Policy is available on request.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "best-execution",
    tag: "CONDUCT",
    tagColor: C.amber,
    icon: "⚡",
    title: "Order Execution Policy",
    severity: "low",
    summary: "Digitalbrutalnft is required to take all sufficient steps to obtain the best possible result for your orders.",
    content: [
      "Digitalbrutalnft operates an Order Execution Policy designed to achieve the best possible result for clients when executing orders, taking into account price, costs, speed, likelihood of execution, size, and nature of the order.",
      "For copy-trading orders, execution is automated and near-instantaneous. Orders are routed to our liquidity providers in sequence according to our Best Execution framework, which prioritises overall best outcome rather than price alone.",
      "We review our Order Execution Policy at least annually and whenever a material change occurs that affects our ability to continue achieving best execution. We will notify you of any material changes to this policy.",
      "You have the right to request information about our Order Execution Policy and how it applied to a specific order you placed. Please contact support@Digitalbrutalnft.io with the order reference and date.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "client-assets",
    tag: "FINANCIAL",
    tagColor: C.green,
    icon: "🔒",
    title: "Client Asset Protection",
    severity: "low",
    summary: "Your funds are held in segregated client money accounts, separate from Digitalbrutalnft's own funds.",
    content: [
      "Digitalbrutalnft holds client money in accordance with the FCA's Client Assets Sourcebook (CASS). Your funds are held in segregated client money accounts at authorised UK credit institutions, entirely separate from Digitalbrutalnft's own operating funds.",
      "In the unlikely event that Digitalbrutalnft were to become insolvent, client money held in segregated accounts would not form part of Digitalbrutalnft's assets and would be returned to clients in accordance with CASS rules.",
      "Eligible clients may also be covered by the Financial Services Compensation Scheme (FSCS) for up to £85,000 per person if Digitalbrutalnft is unable to meet its financial obligations. Full details of FSCS eligibility are available at fscs.org.uk.",
      "Cryptoassets held on your Digitalbrutalnft account are stored using a combination of hot and cold storage infrastructure. Cryptoassets are not covered by FSCS protection. Please refer to our Cryptoasset Risk Disclosure for further details.",
    ],
    updated: "1 May 2025",
  },
  {
    id: "promotions",
    tag: "MARKETING",
    tagColor: C.textMid,
    icon: "📣",
    title: "Financial Promotions",
    severity: "low",
    summary: "All financial promotions on this platform have been approved by Digitalbrutalnft as an FCA-authorised firm.",
    content: [
      "Digitalbrutalnft is responsible for all financial promotions published on our platform, website, and marketing channels. All promotions are reviewed by our compliance team before publication to ensure they are fair, clear, and not misleading, in accordance with FCA COBS 4 requirements.",
      "Performance figures and statistics displayed in financial promotions are based on verified historical data. They are accompanied by past performance disclaimers where required. We do not manufacture, project, or extrapolate performance figures for promotional purposes.",
      "Testimonials and case studies, where used, reflect the genuine experience of real Digitalbrutalnft users. They are not typical results and are clearly labelled as such. We do not pay for or incentivise positive reviews.",
      "If you believe any Digitalbrutalnft promotion is misleading or non-compliant, please report it to compliance@Digitalbrutalnft.io or directly to the FCA at fca.org.uk/consumers/report-financial-scam.",
    ],
    updated: "1 May 2025",
  },
];

const SEVERITY_CONFIG = {
  high:   { label:"HIGH RISK",   color:C.red,    bg:C.redDim,    bar:3 },
  medium: { label:"MEDIUM RISK", color:C.amber,  bg:C.amberDim,  bar:2 },
  low:    { label:"LOW RISK",    color:C.blue,   bg:C.blueDim,   bar:1 },
  info:   { label:"REGULATORY",  color:C.accent, bg:C.accentDim, bar:0 },
};

/* ─────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;0,9..144,800;0,9..144,900;1,9..144,400;1,9..144,500&family=Public+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; height: 100%; }
body {
  background: ${C.bg};
  color: ${C.text};
  font-family: 'Public Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  min-height: 100%;
  overflow-x: hidden;
}
a { text-decoration: none; color: inherit; }

/* ── Animations ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes gridFade {
  from { opacity: 0; }
  to   { opacity: .25; }
}
@keyframes pulse {
  0%,100% { opacity:1; transform:scale(1); }
  50%     { opacity:.4; transform:scale(.85); }
}
@keyframes needleSweep {
  from { transform: rotate(-90deg); }
  to   { transform: rotate(var(--needle-angle)); }
}
@keyframes tickerScroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes blink {
  0%,100% { opacity:1; }
  50%     { opacity:0; }
}

/* ── Grid BG ── */
.grid-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(${C.border} 1px, transparent 1px),
    linear-gradient(90deg, ${C.border} 1px, transparent 1px);
  background-size: 56px 56px;
  animation: gridFade 1.2s ease-out both;
  mask-image: radial-gradient(ellipse 80% 55% at 50% 0%, black 0%, transparent 100%);
}

/* ── Nav ── */
.tf-nav {
  height: 60px;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 clamp(16px, 4vw, 40px);
  background: ${C.bg}F2; backdrop-filter: blur(14px);
  border-bottom: 1px solid ${C.border};
  position: sticky; top: 0; z-index: 100;
}
.tf-nav-links { display:flex; align-items:center; gap:4px; }
.tf-nav-link {
  font-size:13px; font-weight:500; color:${C.textMid};
  padding:6px 12px; border-radius:7px;
  transition:color .15s, background .15s;
}
.tf-nav-link:hover { color:${C.text}; background:rgba(255,255,255,.04); }
.tf-nav-link.active { color:${C.text}; font-weight:600; }
@media(max-width:600px){ .tf-nav-links { display:none !important; } }

/* ── Buttons ── */
.btn-primary {
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:10px 20px; background:${C.accent}; color:#06070B;
  border:none; border-radius:9px; font-size:13px; font-weight:700;
  font-family:'Public Sans',sans-serif; cursor:pointer;
  transition:transform .18s, box-shadow .18s; text-decoration:none;
  position:relative; overflow:hidden;
}
.btn-primary::after {
  content:''; position:absolute; inset:0;
  background:linear-gradient(135deg,rgba(255,255,255,.18) 0%,transparent 60%);
  opacity:0; transition:opacity .2s;
}
.btn-primary:hover { transform:translateY(-2px); box-shadow:0 10px 32px ${C.accentGlow}; }
.btn-primary:hover::after { opacity:1; }

.btn-ghost {
  display:inline-flex; align-items:center; justify-content:center;
  padding:8px 16px; background:transparent; color:${C.textMid};
  border:1px solid ${C.border}; border-radius:8px; font-size:12px; font-weight:600;
  font-family:'Public Sans',sans-serif; cursor:pointer;
  transition:border-color .2s, color .2s; text-decoration:none;
}
.btn-ghost:hover { border-color:${C.borderHover}; color:${C.text}; }

/* ── Risk gauge ── */
.gauge-wrap {
  position: relative;
  width: 220px; height: 120px;
  margin: 0 auto;
}
.gauge-arc-bg {
  fill: none;
  stroke: ${C.border};
  stroke-width: 14;
  stroke-linecap: round;
}
.gauge-arc-fill {
  fill: none;
  stroke-width: 14;
  stroke-linecap: round;
  stroke-dasharray: 251;
  stroke-dashoffset: 0;
  transition: stroke-dashoffset 1.8s cubic-bezier(.22,1,.36,1), stroke .4s;
}
.gauge-needle {
  transform-origin: 50% 85%;
  animation: needleSweep 1.8s cubic-bezier(.22,1,.36,1) .3s both;
}
.gauge-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 11px;
  letter-spacing: .08em;
}

/* ── Ticker tape ── */
.ticker-outer {
  overflow: hidden;
  border-top: 1px solid ${C.border};
  border-bottom: 1px solid ${C.border};
  background: ${C.surface};
  padding: 10px 0;
}
.ticker-inner {
  display: flex; gap: 48px;
  width: max-content;
  animation: tickerScroll 28s linear infinite;
}
.ticker-item {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'IBM Plex Mono', monospace; font-size: 11px;
  color: ${C.textDim}; white-space: nowrap; letter-spacing: .04em;
}
.ticker-item strong { color: ${C.textMid}; font-weight: 500; }

/* ── Disclosure card ── */
.disc-card {
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 10px;
  overflow: hidden;
  transition: border-color .2s, box-shadow .2s;
  scroll-margin-top: 80px;
}
.disc-card:hover { border-color: ${C.borderHover}; }

.disc-card-header {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 24px 26px 20px;
  cursor: pointer;
  transition: background .15s;
}
.disc-card-header:hover { background: rgba(255,255,255,.03); }

.disc-card-body {
  padding: 0 26px 26px;
  animation: fadeIn .22s ease both;
}

.severity-bar {
  display: flex; gap: 3px; align-items: center;
}
.severity-pip {
  width: 20px; height: 4px; border-radius: 2px;
  transition: background .3s;
}

/* ── Tag badge ── */
.disc-tag {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 5px;
  font-family: 'IBM Plex Mono', monospace; font-size: 9px;
  font-weight: 500; letter-spacing: .08em;
  border: 1px solid;
}

/* ── ToC sidebar ── */
.toc-rail {
  position: sticky; top: 80px;
  width: 200px; flex-shrink: 0;
  align-self: flex-start;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 14px;
}
.toc-btn {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 8px;
  font-size: 12px; color: ${C.textMid};
  cursor: pointer; border: none; background: none;
  width: 100%; text-align: left;
  font-family: 'Public Sans', sans-serif;
  transition: color .15s, background .15s;
  line-height: 1.4;
}
.toc-btn:hover { color: ${C.text}; background: rgba(255,255,255,.04); }
.toc-btn.active { color: ${C.accent}; background: ${C.accentDim}; font-weight: 600; }

/* ── Read progress ── */
.read-bar {
  position: fixed; top: 0; left: 0; height: 2px; z-index: 200;
  background: ${C.red};
  transition: width .12s linear;
  box-shadow: 0 0 8px ${C.red};
}

/* ── Language switcher ── */
.lang-switcher { position:relative; display:inline-flex; align-items:center; }
.lang-btn {
  display:inline-flex; align-items:center; gap:6px;
  background:transparent; border:1px solid ${C.border};
  border-radius:8px; padding:6px 10px;
  font-size:12px; font-weight:600; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:border-color .2s, color .2s, background .2s; white-space:nowrap;
}
.lang-btn:hover { border-color:${C.borderHover}; color:${C.text}; background:rgba(255,255,255,.04); }
.lang-dropdown {
  position:absolute; top:calc(100% + 6px); right:0;
  background:${C.surface}; border:1px solid ${C.border};
  border-radius:12px; padding:6px;
  min-width:180px; z-index:400;
  box-shadow:0 16px 40px rgba(0,0,0,.6);
  animation:fadeUp .2s ease both;
}
.lang-option {
  display:flex; align-items:center; gap:10px;
  padding:9px 12px; border-radius:8px;
  font-size:13px; color:${C.textMid};
  cursor:pointer; font-family:'Public Sans',sans-serif;
  transition:background .15s, color .15s;
}
.lang-option:hover { background:${C.surfaceAlt}; color:${C.text}; }
.lang-option.active { color:${C.accent}; background:${C.accentDim}; }
.goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
body { top:0 !important; }
.skiptranslate { display:none !important; }

/* ── Scrollbar ── */
::-webkit-scrollbar { width:5px; }
::-webkit-scrollbar-track { background:${C.bg}; }
::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }

/* ── Responsive ── */
@media(max-width:860px) {
  .toc-rail { display: none !important; }
}
@media(max-width:480px) {
  .disc-card-header { flex-direction: column; gap:12px; }
  .gauge-wrap { width: 180px; height: 100px; }
}
`;

/* ─────────────────────────────────────────────
   LANGUAGE SWITCHER
───────────────────────────────────────────── */
const LANGUAGES = [
  { code:"en",    label:"English",   flag:"🇬🇧" },
  { code:"fr",    label:"Français",  flag:"🇫🇷" },
  { code:"es",    label:"Español",   flag:"🇪🇸" },
  { code:"de",    label:"Deutsch",   flag:"🇩🇪" },
  { code:"pt",    label:"Português", flag:"🇧🇷" },
  { code:"ar",    label:"العربية",   flag:"🇸🇦" },
  { code:"zh-CN", label:"中文",      flag:"🇨🇳" },
  { code:"ja",    label:"日本語",    flag:"🇯🇵" },
  { code:"ru",    label:"Русский",   flag:"🇷🇺" },
  { code:"hi",    label:"हिन्दी",    flag:"🇮🇳" },
  { code:"yo",    label:"Yoruba",    flag:"🇳🇬" },
  { code:"ha",    label:"Hausa",     flag:"🇳🇬" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage:"en", autoDisplay:false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

const LanguageSwitcher = () => {
  const [open, setOpen]       = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref                   = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const select = (code) => { setCurrent(code); localStorage.setItem("tf_lang", code); setOpen(false); applyGoogleTranslate(code); };
  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen(o => !o)}>
        <span style={{fontSize:"14px"}}>{active.flag}</span>
        <span>{active.label}</span>
        <span style={{fontSize:"9px", opacity:.6, marginLeft:"2px"}}>{open?"▲":"▼"}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div style={{fontSize:"10px", fontWeight:"600", color:C.textDim, letterSpacing:".08em", textTransform:"uppercase", padding:"4px 12px 8px"}}>Select Language</div>
          {LANGUAGES.map(l => (
            <div key={l.code} className={"lang-option"+(l.code===current?" active":"")} onClick={()=>select(l.code)}>
              <span style={{fontSize:"16px"}}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code===current && <span style={{marginLeft:"auto", fontSize:"11px"}}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function PublicNav() {
  return (
    <nav className="tf-nav">
      <a href="/" style={{display:"flex", alignItems:"center"}}>
        <img src={logo} alt="Digitalbrutalnft" style={{height:"40px", width:"auto", filter:"invert(1) brightness(2)"}} />
      </a>
      <div className="tf-nav-links">
        <a href="/"            className="tf-nav-link">Home</a>
        <a href="/about"       className="tf-nav-link">About</a>
        <a href="/disclosures" className="tf-nav-link active">Disclosures</a>
      </div>
      <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
        <LanguageSwitcher />
        <a href="/login"  className="btn-ghost">Log in</a>
        <a href="/signup" className="btn-primary">Sign up free →</a>
      </div>
    </nav>
  );
}

/* ─────────────────────────────────────────────
   READ PROGRESS
───────────────────────────────────────────── */
function ReadProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? (el.scrollTop / total) * 100 : 0);
    };
    window.addEventListener("scroll", fn, { passive:true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return <div className="read-bar" style={{width:`${pct}%`}}/>;
}

/* ─────────────────────────────────────────────
   RISK GAUGE  (signature element)
   SVG half-circle needle gauge styled like a
   trading terminal risk indicator
───────────────────────────────────────────── */
function RiskGauge() {
  const [animated, setAnimated] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimated(true), 400); return () => clearTimeout(t); }, []);

  // Arc: cx=110 cy=110 r=80, stroke-dasharray=251 (half-circumference ≈ π*80)
  const CIRC = 251;
  // HIGH = dashoffset 0 (full arc filled)
  const dashOffset = animated ? 0 : CIRC;
  // Needle angle: -90deg = far left (LOW), 0deg = top (MED), 90deg = far right (HIGH)
  const needleAngle = animated ? 82 : -90;

  return (
    <div style={{textAlign:"center"}}>
      <div className="gauge-wrap">
        <svg viewBox="0 0 220 120" style={{width:"100%", height:"100%", overflow:"visible"}}>
          {/* Zone fills behind arc */}
          <defs>
            <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%"   stopColor={C.blue}  stopOpacity=".7"/>
              <stop offset="40%"  stopColor={C.amber} stopOpacity=".8"/>
              <stop offset="100%" stopColor={C.red}   stopOpacity="1"/>
            </linearGradient>
          </defs>

          {/* Background arc */}
          <path
            d="M 30 110 A 80 80 0 0 1 190 110"
            className="gauge-arc-bg"
          />

          {/* Filled arc — animated */}
          <path
            d="M 30 110 A 80 80 0 0 1 190 110"
            fill="none"
            stroke="url(#gaugeGrad)"
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={animated ? 0 : CIRC}
            style={{transition:"stroke-dashoffset 1.8s cubic-bezier(.22,1,.36,1)"}}
          />

          {/* Zone labels */}
          <text x="22"  y="128" className="gauge-label" fill={C.blue}  textAnchor="middle">LOW</text>
          <text x="110" y="40"  className="gauge-label" fill={C.amber} textAnchor="middle">MED</text>
          <text x="198" y="128" className="gauge-label" fill={C.red}   textAnchor="middle">HIGH</text>

          {/* Needle */}
          <g
            className="gauge-needle"
            style={{
              transformOrigin:"110px 110px",
              transform:`rotate(${animated ? needleAngle : -90}deg)`,
              transition:"transform 1.8s cubic-bezier(.22,1,.36,1) .3s",
            }}
          >
            <line x1="110" y1="110" x2="110" y2="42" stroke={C.red} strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="110" cy="110" r="5" fill={C.red}/>
            <circle cx="110" cy="110" r="9" fill="none" stroke={C.red} strokeWidth="1.5" strokeOpacity=".3"/>
          </g>
        </svg>
      </div>

      {/* Label below gauge */}
      <div style={{
        display:"inline-flex", alignItems:"center", gap:"8px",
        background:C.redDim, border:`1px solid ${C.red}40`,
        borderRadius:"8px", padding:"6px 14px", marginTop:"8px",
      }}>
        <span style={{width:"7px", height:"7px", borderRadius:"50%", background:C.red, display:"inline-block", animation:"pulse 1.2s ease-in-out infinite"}}/>
        <span style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"12px", color:C.red, letterSpacing:".08em", fontWeight:"500"}}>
          HIGH RISK — READ BEFORE TRADING
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   REGULATORY TICKER
───────────────────────────────────────────── */
const TICKER_ITEMS = [
  { label:"FCA Firm Ref", val:"987654" },
  { label:"Authorisation", val:"UK FCA" },
  { label:"Company No.", val:"12345678" },
  { label:"Client Money", val:"CASS Protected" },
  { label:"FSCS Cover", val:"Up to £85,000" },
  { label:"Last Updated", val:"1 May 2025" },
  { label:"Jurisdiction", val:"England & Wales" },
  { label:"ICO Reg.", val:"ZA123456" },
];

function RegulatoryTicker() {
  const items = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="ticker-outer">
      <div className="ticker-inner">
        {items.map(({ label, val }, i) => (
          <div key={i} className="ticker-item">
            <span>{label}:</span>
            <strong>{val}</strong>
            <span style={{color:C.border, marginLeft:"24px"}}>◆</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
function Hero() {
  return (
    <div style={{
      textAlign:"center",
      padding:"clamp(52px,8vw,96px) clamp(16px,6vw,48px) clamp(36px,5vw,56px)",
      position:"relative", zIndex:1,
      animation:"fadeUp .7s cubic-bezier(.22,1,.36,1) both",
    }}>
      <div style={{position:"absolute", width:"500px", height:"260px", borderRadius:"50%", background:"rgba(201,105,95,.14)", filter:"blur(110px)", top:0, left:"50%", transform:"translateX(-50%)", pointerEvents:"none"}}/>

      <div style={{position:"relative", zIndex:1}}>
        <div style={{
          display:"inline-flex", alignItems:"center", gap:"6px",
          background:C.redDim, border:`1px solid ${C.red}30`,
          borderRadius:"6px", padding:"3px 10px", marginBottom:"24px",
          fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.red,
          letterSpacing:".06em", textTransform:"uppercase",
        }}>
          <span style={{width:"5px", height:"5px", borderRadius:"50%", background:C.red, display:"inline-block", animation:"pulse 1.2s ease-in-out infinite"}}/>
          Legal & Regulatory Disclosures
        </div>

        <h1 style={{
          fontFamily:"'Fraunces',serif",
          fontSize:"clamp(34px,5.5vw,64px)",
          fontWeight:"600",
          letterSpacing:"-2.5px",
          lineHeight:"1.04",
          color:C.text,
          marginBottom:"18px",
          maxWidth:"640px",
          marginLeft:"auto", marginRight:"auto",
        }}>
          Know the risks.<br/>
          <span style={{fontStyle:"italic", color:C.red}}>Before you trade.</span>
        </h1>

        <p style={{
          fontSize:"clamp(14px,1.8vw,16px)",
          color:C.textMid,
          lineHeight:"1.75",
          maxWidth:"460px",
          marginLeft:"auto", marginRight:"auto",
          marginBottom:"40px",
        }}>
          These disclosures are required by the FCA and provided for your protection.
          Read them. They contain information that could materially affect your trading decisions.
        </p>

        <RiskGauge />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SEVERITY BAR COMPONENT
───────────────────────────────────────────── */
function SeverityBar({ severity }) {
  const cfg = SEVERITY_CONFIG[severity];
  const pips = [1,2,3];
  return (
    <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
      <div className="severity-bar">
        {pips.map(p => (
          <div
            key={p}
            className="severity-pip"
            style={{ background: p <= cfg.bar ? cfg.color : C.border }}
          />
        ))}
      </div>
      <span style={{
        fontFamily:"'IBM Plex Mono',monospace", fontSize:"9px",
        color: cfg.color, letterSpacing:".07em", fontWeight:"500",
      }}>{cfg.label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DISCLOSURE CARD
───────────────────────────────────────────── */
function DiscCard({ disc, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const cfg = SEVERITY_CONFIG[disc.severity];

  return (
    <div id={disc.id} className="disc-card">
      {/* Top severity stripe */}
      <div style={{height:"3px", background:`linear-gradient(90deg, ${cfg.color}, transparent)`}}/>

      <div className="disc-card-header" onClick={() => setOpen(o => !o)}>
        {/* Icon */}
        <div style={{
          width:"44px", height:"44px", borderRadius:"12px", flexShrink:0,
          background:cfg.bg, border:`1px solid ${cfg.color}30`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:"20px",
        }}>{disc.icon}</div>

        {/* Title block */}
        <div style={{flex:1, minWidth:0}}>
          <div style={{display:"flex", alignItems:"center", gap:"8px", flexWrap:"wrap", marginBottom:"6px"}}>
            <span
              className="disc-tag"
              style={{color:disc.tagColor, borderColor:`${disc.tagColor}40`, background:`${disc.tagColor}10`}}
            >
              {disc.tag}
            </span>
            <SeverityBar severity={disc.severity}/>
          </div>
          <div style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(15px,1.8vw,17px)", fontWeight:"800", color:C.text, letterSpacing:"-0.3px", marginBottom:"5px"}}>
            {disc.title}
          </div>
          <div style={{fontSize:"12px", color:C.textMid, lineHeight:"1.5"}}>
            {disc.summary}
          </div>
        </div>

        {/* Toggle */}
        <div style={{
          flexShrink:0, fontSize:"11px", color:C.textDim, fontWeight:"600",
          transition:"transform .22s", transform: open ? "rotate(180deg)" : "rotate(0deg)",
          marginTop:"4px",
        }}>▼</div>
      </div>

      {open && (
        <div className="disc-card-body">
          {/* Divider */}
          <div style={{height:"1px", background:C.border, marginBottom:"20px"}}/>
          <div style={{display:"flex", flexDirection:"column", gap:"12px"}}>
            {disc.content.map((para, i) => (
              <p key={i} style={{fontSize:"13px", color:C.textMid, lineHeight:"1.8"}}>
                {para}
              </p>
            ))}
          </div>
          <div style={{
            marginTop:"20px", padding:"10px 14px",
            background:C.surfaceAlt, borderRadius:"8px",
            display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"8px",
          }}>
            <span style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.textDim, letterSpacing:".06em"}}>
              LAST UPDATED: {disc.updated}
            </span>
            <span style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:"10px", color:C.textDim, letterSpacing:".06em", animation:"blink 1.5s ease-in-out infinite"}}>
              ● CURRENT
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOC SIDEBAR
───────────────────────────────────────────── */
function TocRail({ activeId }) {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });
  return (
    <div className="toc-rail">
      <div style={{fontSize:"9px", fontFamily:"'IBM Plex Mono',monospace", color:C.textDim, letterSpacing:".1em", textTransform:"uppercase", marginBottom:"10px", paddingLeft:"10px"}}>
        Disclosures
      </div>
      {DISCLOSURES.map(d => {
        const cfg = SEVERITY_CONFIG[d.severity];
        return (
          <button key={d.id} className={"toc-btn" + (activeId === d.id ? " active" : "")} onClick={() => scrollTo(d.id)}>
            <span style={{width:"6px", height:"6px", borderRadius:"50%", background: activeId===d.id ? C.accent : cfg.color, flexShrink:0, opacity: activeId===d.id ? 1 : .5}}/>
            {d.title}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ACTIVE SECTION TRACKER
───────────────────────────────────────────── */
function useActiveSection() {
  const [activeId, setActiveId] = useState(DISCLOSURES[0].id);
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveId(e.target.id); }),
      { rootMargin:"-20% 0px -60% 0px" }
    );
    DISCLOSURES.forEach(d => { const el = document.getElementById(d.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  return activeId;
}

/* ─────────────────────────────────────────────
   FOOTER NOTICE
───────────────────────────────────────────── */
function FooterNotice() {
  return (
    <div style={{maxWidth:"1060px", margin:"0 auto", padding:"0 clamp(16px,4vw,40px) clamp(72px,9vw,110px)"}}>
      <div style={{
        background:C.surface, border:`1px solid ${C.border}`,
        borderTop:`2px solid ${C.red}`,
        borderRadius:"10px", padding:"clamp(24px,3.5vw,36px)",
        position:"relative", overflow:"hidden",
      }}>
        <div style={{position:"absolute", width:"300px", height:"200px", borderRadius:"50%", background:"rgba(201,105,95,.10)", filter:"blur(70px)", bottom:"-60px", right:"0", pointerEvents:"none"}}/>
        <div style={{position:"relative", zIndex:1, display:"flex", alignItems:"flex-start", gap:"20px", flexWrap:"wrap"}}>
          <div style={{fontSize:"28px", flexShrink:0}}>⚠️</div>
          <div style={{flex:1, minWidth:"220px"}}>
            <div style={{fontFamily:"'Fraunces',serif", fontSize:"clamp(15px,2vw,17px)", fontWeight:"800", color:C.text, marginBottom:"10px", letterSpacing:"-0.3px"}}>
              Important: these disclosures do not constitute financial advice
            </div>
            <p style={{fontSize:"13px", color:C.textMid, lineHeight:"1.75", marginBottom:"10px"}}>
              Digitalbrutalnft Technologies Ltd is not authorised to provide personal financial advice. The information on this page is provided for regulatory transparency and informational purposes only. Nothing here should be construed as a recommendation to buy, sell, or hold any financial instrument.
            </p>
            <p style={{fontSize:"13px", color:C.textMid, lineHeight:"1.75"}}>
              If you have questions about these disclosures, contact our compliance team at{" "}
              <a href="mailto:compliance@Digitalbrutalnft.io" style={{color:C.accent, fontWeight:"600"}}>compliance@Digitalbrutalnft.io</a>
              {" "}or the FCA at{" "}
              <a href="https://fca.org.uk" target="_blank" rel="noreferrer" style={{color:C.accent, fontWeight:"600"}}>fca.org.uk</a>.
            </p>
          </div>
          <div style={{display:"flex", gap:"10px", flexShrink:0, flexWrap:"wrap"}}>
            <a href="/terms"   className="btn-ghost"   style={{padding:"10px 16px", fontSize:"12px"}}>Terms</a>
            <a href="/policy"  className="btn-ghost"   style={{padding:"10px 16px", fontSize:"12px"}}>Privacy</a>
            <a href="/signup"  className="btn-primary" style={{padding:"10px 20px", fontSize:"13px"}}>Get started →</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   PAGE
───────────────────────────────────────────── */
const Disclosures = () => {
  const activeId = useActiveSection();

  return (
    <>
      <style>{CSS}</style>
      <ReadProgress />
      <div className="grid-bg"/>
      <PublicNav/>
      <Hero/>
      <RegulatoryTicker/>

      {/* Main body */}
      <div style={{
        maxWidth:"1060px", margin:"0 auto",
        padding:"clamp(36px,5vw,56px) clamp(16px,4vw,40px) clamp(32px,4vw,48px)",
        display:"flex", gap:"24px", alignItems:"flex-start",
        position:"relative", zIndex:1,
      }}>
        {/* ToC sidebar (desktop only) */}
        <div style={{display:"none"}} className="disc-toc-wrap">
          <TocRail activeId={activeId}/>
        </div>
        <style>{`@media(min-width:860px){ .disc-toc-wrap{ display:block !important; } }`}</style>

        {/* Cards */}
        <div style={{flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:"12px"}}>
          {DISCLOSURES.map((d, i) => (
            <div key={d.id} style={{animation:`fadeUp .55s cubic-bezier(.22,1,.36,1) ${.05+i*.07}s both`}}>
              <DiscCard disc={d} defaultOpen={i === 0}/>
            </div>
          ))}
        </div>
      </div>

      <FooterNotice/>
    </>
  );
};

export default Disclosures;